import React, { useEffect, useState } from "react";
import ApiURL from "../controllers/Api";
import AdminLayout from "./AdminLayout";
import CompanyLayout from "./CompanyLayout";
import { toast } from "../utils/toast";

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem("token");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const handleJson = async (response) => {
  const body = await response.json().catch(() => ({}));
  return {
    success: response.ok && body.success !== false,
    body,
    message: body.message || "",
  };
};

export default function EmployeeCreate() {
  const isCompanyView = localStorage.getItem("userType") === "company";
  const Layout = isCompanyView ? CompanyLayout : AdminLayout;
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    job_role: "Recruiter",
    city: "",
    state: "",
    address: "",
    password: "Welcome@123",
    can_view_dashboard: true,
    can_upload_resumes: true,
    can_post_jobs: true,
    can_view_resume_library: false,
    can_manage_sub_companies: false,
  });

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${ApiURL}crm/admin/create_employee.php`, {
        method: "POST",
        headers: getAuthHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify(form),
      });

      const result = await handleJson(response);
      if (!result.success) {
        setMessage(result.message || "Failed to create recruiter.");
        toast.error(result.message || "Failed to create recruiter.");
        return;
      }

      const employee = result.body?.data?.employee || result.body?.employee;
      setMessage(
        employee
          ? `Recruiter created successfully. Personnel ID: ${employee.personnel_id}`
          : "Recruiter created successfully."
      );
      toast.success(
        employee
          ? `Recruiter created successfully. Personnel ID: ${employee.personnel_id}`
          : "Recruiter created successfully."
      );
      setForm((prev) => ({
        ...prev,
        name: "",
        email: "",
        mobile: "",
        city: "",
        state: "",
        address: "",
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout
      title="Add Recruiter"
      description={
        isCompanyView
          ? "Create recruiter accounts for your company team and assign resumes to them."
          : "Create the recruiter first, then assign resumes from the assign page."
      }
    >
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        {message ? (
          <div className="mt-4 rounded-xl bg-blue-50 px-4 py-3 text-sm text-blue-700">
            {message}
          </div>
        ) : null}

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Full Name</label>
              <input name="name" value={form.name} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
              <input name="email" type="email" value={form.email} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Phone</label>
              <input name="mobile" value={form.mobile} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Designation</label>
              <input name="job_role" value={form.job_role} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" />
            </div>
          </div>

          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Password</label>
              <input name="password" value={form.password} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">City</label>
              <input name="city" value={form.city} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">State</label>
              <input name="state" value={form.state} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Address</label>
            <textarea name="address" value={form.address} onChange={handleChange} className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" rows={3} />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {[
              ["can_view_dashboard", "Dashboard"],
              ["can_upload_resumes", "Upload Resumes"],
              ["can_post_jobs", "Post Job"],
              ["can_view_resume_library", "Resume Library"],
              ["can_manage_sub_companies", "Sub Companies"],
            ].map(([name, label]) => (
              <label key={name} className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">
                <input
                  type="checkbox"
                  name={name}
                  checked={Boolean(form[name])}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                {label}
              </label>
            ))}
          </div>
          <div>
            <button type="submit" disabled={loading} className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">
              {loading ? "Creating..." : "Create Recruiter"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
